import clsx from "clsx";
import { motion } from "motion/react";
import { Trans } from "react-i18next";
import { Icon } from "@/ui/icon/Icon";
import { HelpIcon } from "@/ui/icon/icons/help-icon/HelpIcon";
import { RadioIcon } from "@/ui/icon/icons/radio-icon/RadioIcon";
import { Tooltip } from "@/ui/tooltip/Tooltip";
import { useMedia } from "@/hooks/useMedia";
import { insertBetween } from "@/utils/array";
import { createIsolatedEventHandler } from "@/utils/dom";
import { formatPercent, getCurrencyParts } from "@/utils/number";
import { useCalculator } from "../../hooks/useCalculator";
import { useCalculatorUpdate } from "../../hooks/useCalculatorUpdate";
import type { Calc } from "../../types";
import { CalculatorSelect } from "../calculator-select/CalculatorSelect";
import styles from "./CalculatorPack.module.css";

export const CalculatorPack: React.FC<{
  pack: Calc.Pack.Pack;
}> = ({ pack }) => {
  const active = useCalculator((state) => state.activePack === pack.id);
  const isSm = useMedia("<sm");

  if (isSm === true) {
    return (
      <motion.div
        initial={{
          height: active ? "auto" : 72,
        }}
        animate={{
          height: active ? "auto" : 72,
        }}
        exit={{ height: 72 }}
        transition={{ type: "tween", duration: 0.5 }}
      >
        <Pack pack={pack} />
      </motion.div>
    );
  }

  return <Pack pack={pack} />;
};

const Pack: React.FC<{
  pack: Calc.Pack.Pack;
}> = ({ pack }) => {
  const active = useCalculator((state) => state.activePack === pack.id);
  const setActivePack = useCalculator((state) => state.setActivePack);
  const setCustomActive = useCalculator((state) => state.setCustomActive);
  const { data } = useCalculatorUpdate();

  const { price, info } = data.packs.find((p) => p.id === pack.id) ?? {
    price: pack.price,
    info: pack.info,
  };

  const sm = useMedia("<sm");

  return (
    <div
      className={clsx(styles.pack, styles[pack.color], active && styles.active)}
      onClick={() => setActivePack(pack.id)}
    >
      <div className={styles.header}>
        <RadioIcon className={styles.radio} active={active} />
        {pack.badge !== undefined && (
          <div className={styles.badge}>{pack.badge}</div>
        )}
      </div>

      <div className={styles.title}>{pack.title}</div>
      {price.discount !== undefined && (
        <div className={styles.discount}>{formatPercent(price.discount)}</div>
      )}
      <PackPrice price={price} />

      <div className={styles.group}>
        {sm === true && price.label !== undefined && (
          <PackPriceLabel label={price.label} />
        )}

        {info !== undefined && (
          <div className={styles.info}>
            <div className={styles.infoText}>{info}</div>
            {pack.type === "module" && (
              <div
                className={styles.infoLink}
                onClick={createIsolatedEventHandler(() =>
                  setCustomActive(true),
                )}
              >
                <Trans>Add More</Trans>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.modules}>
        {pack.modules.map((packModule, index) => (
          <Module key={index} {...packModule} />
        ))}
      </div>
      {pack.includedModules !== undefined && (
        <div className={styles.includedModulesList}>
          <div className={styles.includedModulesListHeader}>
            <div className={styles.includedModulesListHeading}>
              <Trans>What you get</Trans>
            </div>
            <div className={styles.includedModulesListHeading}>
              <Trans>Profiles limits</Trans>
            </div>
          </div>
          {pack.includedModules.map((includedModule, index) => {
            switch (includedModule.type) {
              case "default":
                return <PackIncludedModule key={index} {...includedModule} />;
              case "select":
                return (
                  <PackIncludedConfigurableModule
                    key={index}
                    {...includedModule}
                  />
                );
            }
          })}
        </div>
      )}
    </div>
  );
};

interface PackPriceProps {
  price: Calc.Price.Price;
}

const PackPrice: React.FC<PackPriceProps> = ({ price }) => {
  const parts = getCurrencyParts(price.value, price.currency);

  return (
    <div className={styles.price}>
      <div className={styles.currency}>{parts.currency}</div>
      <div className={styles.value}>
        {parts.plusSign}
        {parts.minusSign}
        <div className={styles.integer}>{parts.integer}</div>
        <div className={styles.decimal}>{parts.decimal}</div>
        <div className={styles.fraction}>{parts.fraction}</div>
      </div>
      {price.discount !== undefined && (
        <div className={styles.discount}>{formatPercent(price.discount)}</div>
      )}
      {price.period !== undefined && (
        <div className={styles.period}>{price.period}</div>
      )}
      {price.label !== undefined && <PackPriceLabel label={price.label} />}
    </div>
  );
};

const PackPriceLabel: React.FC<{
  label: Calc.Price.Label[];
}> = ({ label }) => {
  const labelParts = insertBetween(label, () => {
    return {
      type: "text",
      text: " ",
    };
  });

  return (
    <div className={styles.priceLabel}>
      {labelParts.map((part, index) => (
        <span key={index} className={styles[part.type]}>
          {part.text}
        </span>
      ))}
    </div>
  );
};

const Module: React.FC<Calc.Pack.Module> = ({ active, title, value }) => {
  return (
    <div className={clsx(styles.module, active && styles.active)}>
      <Icon
        className={styles.moduleIcon}
        name={active ? "confirm_24px_filled" : "cancel_24px_regular"}
      />
      <div className={styles.moduleTitle}>{title}</div>
      {value !== undefined && <div className={styles.moduleValue}>{value}</div>}
    </div>
  );
};

const PackIncludedModule: React.FC<Calc.Pack.IncludedModule> = ({
  items,
  value,
}) => {
  return (
    <div className={styles.includedModule}>
      <div className={styles.includedModuleHeader}>
        {items.map((item, index) => (
          <div key={index} className={styles.includedModuleItem}>
            <Icon
              className={styles.includedModuleIcon}
              name="check_mark_16px"
            />
            <div className={styles.includedModuleTitle}>{item.title}</div>
            {item.tooltip !== undefined && (
              <Tooltip>
                <Tooltip.Toggle>
                  <HelpIcon color="grey" />
                </Tooltip.Toggle>
                <Tooltip.Popup>{item.tooltip}</Tooltip.Popup>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      {value !== undefined && (
        <div className={styles.includedModuleBody}>
          <div className={styles.includedModuleValue}>{value}</div>
        </div>
      )}
    </div>
  );
};

const PackIncludedConfigurableModule: React.FC<
  Calc.Pack.IncludedConfigurableModule
> = ({ id, items, select }) => {
  return (
    <div className={styles.includedModule}>
      <div className={styles.includedModuleHeader}>
        {items.map((item, index) => (
          <div key={index} className={styles.includedModuleItem}>
            <Icon
              className={styles.includedModuleIcon}
              name="check_mark_16px"
            />
            <div className={styles.includedModuleTitle}>{item.title}</div>
            {item.tooltip !== undefined && (
              <Tooltip>
                <Tooltip.Toggle>
                  <HelpIcon color="grey" />
                </Tooltip.Toggle>
                <Tooltip.Popup>{item.tooltip}</Tooltip.Popup>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      <div className={styles.includedModuleBody}>
        <CalculatorSelect key={select.id} moduleId={id} select={select} />
      </div>
    </div>
  );
};
